import React, {useCallback, useEffect, useState} from 'react';
import Box from "@mui/material/Box";
import {FormControl, InputAdornment, InputLabel, OutlinedInput} from "@mui/material";
import {useLazyQuery} from "@apollo/client";
import {CircularProgress, Link, Typography} from "@mui/joy";
import {SearchRounded, SearchOffRounded} from "@mui-symbols-material/w300";
import {Asset, AssetProposalConfidence} from "../../../codegen/graphql";
import AssetSearchResult from "../assetSearchResult";
import ProposalSearchResult, {SerialLookupProposal} from "../proposalSearchResult";
import _ from "lodash";
import SearchAssetEmptyView from "../emptyView";
import {useTranslation} from "react-i18next";
import {SerialNumberLookup} from "../../../graphql/queries/serialNumberLookup";
import {CatalogProductNode} from "@dreebit-registry/vsm-microservice-components";

const MIN_QUERY_LENGTH = 4;

interface Props {
    selectedAssetId?: string;
    initialQuery?: string;
    onAssetSelect: (asset: Asset | null) => void;
    onSerialNumberChange: (serialNumber: string) => void;
    /** Hide this asset id from the rendered results (e.g. the source asset in ASSET_EXCHANGE). */
    excludeAssetId?: string;
    /** Empty-state CTA: clicked when the user has searched and gotten no asset hits. */
    onNavigateToProductSearch?: () => void;
    /** EXACT proposal clicked: register the asset directly from the proposal data. */
    onProposalRegister?: (proposal: SerialLookupProposal) => void;
    /** NEEDS_CONFIRMATION proposal clicked: confirm the proposed product in the browser. */
    onProposalConfirm?: (proposal: SerialLookupProposal) => void;
    /** EXACT direct registration in flight (mutation lives in the parent modal). */
    registeringProposal?: boolean;
}

const AssetSearchStep = ({selectedAssetId, initialQuery, onAssetSelect, onSerialNumberChange, excludeAssetId, onNavigateToProductSearch, onProposalRegister, onProposalConfirm, registeringProposal}: Props) => {

    const {t} = useTranslation();
    const [searchQuery, setSearchQuery] = useState<string>(initialQuery ?? '');
    // True from the moment the user types a long-enough query until the lazy
    // query actually starts loading. Bridges the debounce gap so the empty
    // "no results" view doesn't flash before the request fires.
    const [awaitingSearch, setAwaitingSearch] = useState(false);

    const [searchExact, {data: exactAssetData, loading: loadingExactAsset}] = useLazyQuery(SerialNumberLookup, {
        fetchPolicy: "network-only",
    });

    const runSearch = useCallback((query: string) => {
        searchExact({
            variables: {
                serialNumber: query,
                imageCrop: "fit",
                imageWidth: 200,
                imageHeight: 200,
            },
        });
    }, [searchExact]);

    const debouncedSearch = useCallback(
        _.debounce((query: string) => {
            if (!query || query.length < MIN_QUERY_LENGTH) return;
            runSearch(query);
        }, 800),
        [runSearch]
    );

    // The lazy query starts empty after every mount, so a prefilled query
    // (returning from the product browser step) would show "no results" until
    // the user retypes. Fire the lookup for it immediately — no debounce needed.
    useEffect(() => {
        if (searchQuery.length >= MIN_QUERY_LENGTH) {
            runSearch(searchQuery);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchQuery(value);
        onSerialNumberChange(value);
        onAssetSelect(null);
        if (value.length >= MIN_QUERY_LENGTH) {
            setAwaitingSearch(true);
        } else {
            setAwaitingSearch(false);
            debouncedSearch.cancel();
        }
        debouncedSearch(value);
    };

    // Once the lazy query has started loading, it takes over the loading
    // indication; clear awaitingSearch so we don't double-track.
    useEffect(() => {
        if (loadingExactAsset && awaitingSearch) {
            setAwaitingSearch(false);
        }
    }, [loadingExactAsset, awaitingSearch]);

    const lookup = exactAssetData?.serialNumberLookup;
    const exactAssets: Asset[] = ((lookup?.registeredAssets as Asset[] | null | undefined) ?? [])
        .filter((asset) => !excludeAssetId || asset.id !== excludeAssetId);

    // Proposal branch: the serial is not registered, but the lookup matched it in
    // the systems of record. Guard on the product node — a proposal without a
    // catalog node can't be acted on (schema promises it for these confidences).
    const proposedProductNode = lookup?.proposedProductNode as CatalogProductNode | null | undefined;
    const proposal: SerialLookupProposal | null =
        lookup && proposedProductNode &&
        (lookup.confidence === AssetProposalConfidence.EXACT || lookup.confidence === AssetProposalConfidence.NEEDS_CONFIRMATION)
            ? {
                confidence: lookup.confidence,
                serialNumber: lookup.serialNumber,
                dateOfManufacture: lookup.dateOfManufacture,
                manufacturer: lookup.manufacturer,
                proposedProductNode,
            }
            : null;

    const queryActive = searchQuery.length >= MIN_QUERY_LENGTH;
    const isLoading = queryActive && (awaitingSearch || loadingExactAsset);
    const hasAnyResults = exactAssets.length > 0;

    const showEmptyInitial = !queryActive;
    const showEmptyNoResults = queryActive && !isLoading && !hasAnyResults && !proposal;
    const hasResults = queryActive && !isLoading && hasAnyResults;
    // registeredAssets non-empty and a proposal are mutually exclusive per schema,
    // but keep the guard so a backend change can't render both.
    const showProposal = queryActive && !isLoading && !hasAnyResults && Boolean(proposal);

    const handleProposalClick = () => {
        if (!proposal || registeringProposal) return;
        if (proposal.confidence === AssetProposalConfidence.EXACT) {
            onProposalRegister?.(proposal);
        } else {
            onProposalConfirm?.(proposal);
        }
    };

    return <Box className={'flex-column'} sx={{flexGrow: 1, gap: '21px', minHeight: 0, minWidth: 0, maxWidth: '100%'}}>
        <FormControl variant="outlined" sx={{width: "100%"}}>
            <InputLabel>{t('Serial No.')}</InputLabel>
            <OutlinedInput
                sx={{width: "100%"}}
                startAdornment={<InputAdornment position={'start'}><SearchRounded/></InputAdornment>}
                label={t('Serial No.')}
                type="text"
                autoFocus
                value={searchQuery}
                onChange={handleInputChange}
            />
        </FormControl>
        <Box sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
        }}>
            {showEmptyInitial && (
                <SearchAssetEmptyView
                    icon={<SearchRounded/>}
                    title={t('Find your asset')}
                    subtitle={t('Search for assets by serial number. To get results, the full serial number must be entered exactly as shown on the nameplate of your product.')}
                    extra={onNavigateToProductSearch && (
                        <Typography level={'bodySmall'} sx={{textAlign: 'center'}}>
                            {t("Don't know the serial number?")}{' '}
                            <Link component={'button'} onClick={onNavigateToProductSearch}>
                                {t('Search the product catalog')}
                            </Link>
                        </Typography>
                    )}
                />
            )}
            {showEmptyNoResults && (
                <SearchAssetEmptyView
                    icon={<SearchOffRounded/>}
                    title={t("We couldn't find any results")}
                    subtitle={t('You can also manually add the product you are looking for by providing more information.')}
                    btnText={onNavigateToProductSearch ? t('Search products') : undefined}
                    onClick={onNavigateToProductSearch}
                />
            )}
            {isLoading && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    flexGrow: 1,
                    minHeight: 0,
                }}>
                    <CircularProgress/>
                </Box>
            )}
            {showProposal && proposal && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '13px',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: '4px',
                }}>
                    <ProposalSearchResult
                        proposal={proposal}
                        loading={registeringProposal}
                        onClick={handleProposalClick}
                    />
                    {onNavigateToProductSearch && (
                        <Typography level={'bodySmall'} sx={{textAlign: 'center'}}>
                            {t('Not the right product?')}{' '}
                            <Link component={'button'} onClick={onNavigateToProductSearch}>
                                {t('Search the product catalog')}
                            </Link>
                        </Typography>
                    )}
                </Box>
            )}
            {hasResults && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '13px',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: '4px',
                }}>
                    {exactAssets.map((asset) => (
                        <AssetSearchResult
                            key={asset.id}
                            asset={asset}
                            selected={selectedAssetId === asset.id}
                            onAssetSelect={() => onAssetSelect(asset)}
                        />
                    ))}
                </Box>
            )}
        </Box>
    </Box>;
};

export default AssetSearchStep;
